import { useEffect, useState, useRef } from "react";
import PreviewPdfModal from "./PreviewPdfModal";
import CloseIcon from "../../../assets/crossIcon.svg";
import EyeIcon from "../../../assets/eyeIcon.svg";
import EditIcon from "../../../assets/editIcon.svg";
import DownloadIcon from "../../../assets/downloadIconGreen.svg";
import DownloadedIcon from "../../../assets/downloadedIcon.svg";
import { AuthServices } from "../../../services/AuthServices";
import downloadPdf from "../../../utils/downloadPdfUtil";
import Modal from "@mui/material/Modal";
import Close from "../../../assets/crossIcon.svg";
import axios from "axios";
import JsPDF from "jspdf";
import { CircularProgress } from "@mui/material";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
const DownloadMultipleDocuments = ({
  setOpen,
  images,
  documentName,
  tmsVersion,
  hideCompressedDownload = false,
}) => {
  const [documents, setDocuments] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const [netSpeed, setNetSpeed] = useState(0);
  const [nonDownloadedSelect, setNonDownloadedSelect] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDocEdit, setSelectedDocEdit] = useState(null);
  const [pdfCount, setPdfCount] = useState(0);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(20);
  const [rangeDownload, setRangeDownload] = useState(false);
  const [rangePreview, setRangePreview] = useState(false);
  const [errorFrom, setErrorFrom] = useState("");
  const [errorTo, setErrorTo] = useState("");
  const dragDocument = useRef();
  const draggedOverDocument = useRef();
  const handleSelection = (index) => {
    const modifiedData = documents?.map((document, idx) => {
      setSelectAll(false); // This is done to uncheck selectAll if any document is unchecked
      if (index === idx) {
        return { ...document, isSelected: !document.isSelected };
      } else {
        return document;
      }
    });
    setDocuments(modifiedData);
    //counting the selected documents
    const selectedDoc = modifiedData?.filter((data) => data.isSelected);
    setSelectedCount(selectedDoc.length);
  };
  const handleSort = () => {
    let documentsClone = [...documents];

    if (dragDocument.current < draggedOverDocument.current) {
      documentsClone.splice(
        draggedOverDocument.current,
        0,
        documentsClone[dragDocument.current],
      );
      documentsClone.splice(dragDocument.current, 1);
    } else {
      documentsClone.splice(
        draggedOverDocument.current,
        0,
        documentsClone[dragDocument.current],
      );
      documentsClone.splice(dragDocument.current + 1, 1);
    }
    setDocuments(documentsClone);
  };
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setNonDownloadedSelect(false);
    if (selectAll) {
      const modifiedData = documents?.map((document) => ({
        ...document,
        isSelected: false,
      }));
      setDocuments(modifiedData);
      setSelectedCount(0);
    } else {
      const modifiedData = documents?.map((document) => ({
        ...document,
        isSelected: true,
      }));
      setDocuments(modifiedData);
      setSelectedCount(modifiedData.length);
    }
  };

  const handleNonDownloadedSelection = () => {
    setNonDownloadedSelect(!nonDownloadedSelect);
    setSelectAll(false);
    if (nonDownloadedSelect) {
      const modifiedData = documents?.map((document) => ({
        ...document,
        isSelected: false,
      }));
      setDocuments(modifiedData);
      setSelectedCount(0);
    } else {
      let selectCount = 0;
      const modifiedData = documents?.map((document) => {
        if (document.isDownloaded) {
          return { ...document, isSelected: false };
        } else {
          selectCount++;
          return { ...document, isSelected: true };
        }
      });
      setDocuments(modifiedData);
      setSelectedCount(selectCount);
    }
  };

  // Returns the lowercase file extension of a URL (ignoring query/hash)
  const getFileExtension = (url) => {
    if (!url || typeof url !== "string") return "";
    const cleanUrl = url.split(/[?#]/)[0];
    const lastSegment = cleanUrl.split("/").pop() || "";
    const dotIndex = lastSegment.lastIndexOf(".");
    if (dotIndex === -1) return "";
    return decodeURIComponent(lastSegment.slice(dotIndex + 1)).toLowerCase();
  };

  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

  // Downloads a PDF document directly (without going through the API)
  const downloadPdfDirectly = async (url, fileName) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Direct PDF download failed, opening in new tab:", err);
      // Fallback (e.g. CORS restrictions): let the browser handle the URL
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const generatePDF = async (images, compression) => {
    setIsSubmitting(true);
    const selectedDocs = images.filter((image) => image.isSelected);

    // Only jpg/jpeg/png go to the API; pdfs are downloaded directly
    const selectedImages = selectedDocs.filter((doc) =>
      IMAGE_EXTENSIONS.includes(getFileExtension(doc.documentUrl)),
    );
    const selectedPdfs = selectedDocs.filter(
      (doc) => getFileExtension(doc.documentUrl) === "pdf",
    );

    const imagesUrl = selectedImages.map((image) => ({
      image: image.documentUrl,
      compressionPossible: compression,
    }));

    try {
      // Download pdf documents directly
      for (let i = 0; i < selectedPdfs.length; i++) {
        await downloadPdfDirectly(
          selectedPdfs[i].documentUrl,
          `${documentName}_${i + 1}.pdf`,
        );
      }

      // Generate a single PDF from the images via API
      if (imagesUrl.length > 0) {
        let response = await AuthServices.loadImagesBase64(
          { images: imagesUrl },
          setPercentage,
          setNetSpeed,
        );
        downloadPdf(response.data, `${documentName}_${pdfCount + 1}`);
        setPdfCount(pdfCount + 1);
      }

      //setting isDownloaded true for downloaded images
      const modifiedData = documents?.map((document) => {
        if (document.isSelected) {
          return { ...document, isDownloaded: true, isSelected: false };
        } else {
          return document;
        }
      });
      setDocuments(modifiedData);
      setSelectAll(false);
      setSelectedCount(0);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setIsSubmitting(false);
  };

  const generatePDFUsingPython = async (images, compression, from, to) => {
    setIsSubmitting(true);

    // If from and to are both not provided (preview case),
    // use all selected images for PDF generation
    let selectedImages;
    if (!from && !to) {
      selectedImages = images.filter((image) => image.isSelected);
    } else {
      // For batch download, require both fields to be filled
      if (!from || !to) {
        window.alert("From and To field should not be blank");
        setIsSubmitting(false);
        return;
      }
      selectedImages = images.slice(from - 1, to);
    }

    const imagesUrl = selectedImages.map((image) => ({
      image: image.documentUrl,
      compressionPossible: compression,
    }));

    try {
      const targetSize = tmsVersion === "1.0" ? 500 : 1024;
      let response = await AuthServices.loadImagesBase64UsingPython(
        { images: imagesUrl },
        setPercentage,
        setNetSpeed,
        targetSize,
      );

      downloadPdf(response.data, `${documentName} ${pdfCount + 1}`);

      setPdfCount(pdfCount + 1);
      //setting isDownloaded true for downloaded images
      const modifiedData = documents?.map((document) => {
        if (document.isSelected) {
          return { ...document, isDownloaded: true, isSelected: false };
        } else {
          return document;
        }
      });
      setDocuments(modifiedData);
      setSelectAll(false);
      setSelectedCount(0);
    } catch (error) {
      handleApiErrorResponses(error);
    }

    setIsSubmitting(false);
  };

  const handleDownload = (compression) => {
    if (selectedCount > 0) {
      generatePDF(documents, compression);
    } else {
      window.alert("Plese select atleast one image");
    }
  };

  const handleDownloadUsingPyhton = (compression, from, to) => {
    console.log("from:", from, "to", to);
    generatePDFUsingPython(documents, compression, from, to);
    // if (selectedCount > 0) {
    //   generatePDFUsingPython(documents, compression);
    // } else {
    //   window.alert("Plese select atleast one image");
    // }
  };

  const handlePreview = () => {
    if (selectedCount > 0) {
      setPreviewOpen(true);
    } else {
      window.alert("Please select atleast one image");
    }
  };

  const handleNumberChange = (value, setter, errorSetter) => {
    if (/^\d{0,3}$/.test(value)) {
      setter(value);
      errorSetter(""); // Clear error
    } else {
      errorSetter("Only 0–999 allowed");
    }
  };

  const blockInvalidChars = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const modifiedData = images.map((image) => ({
      ...image,
      isSelected: true,
      isDownloaded: false,
    }));
    setDocuments(modifiedData);
    setSelectedCount(modifiedData.length);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  console.log("tms version:", tmsVersion);
  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-[#017c5780] flex items-center justify-center z-[1207]">
      <div
        style={{ border: "1px solid #E4E4E4" }}
        className="relative w-[90%] xl:w-[90%] h-[70%] 2xl:w-[70%] bg-white  py-8 px-12 rounded-[16px] overflow-hidden"
      >
        <div
          className="absolute top-4 right-4 flex justify-end cursor-pointer"
          onClick={() => setOpen(false)}
        >
          <img
            src={CloseIcon}
            alt="close-icon"
            onClick={() => setOpen(false)}
          />
        </div>
        <div className="text-2xl font-semibold text-center text-[#191919] ">
          Select multiple documents & download them as a PDF
        </div>
        <div className="text-[#797979] mt-4 text-center">
          PDFs Downloaded {pdfCount}/5
        </div>
        <div className="flex justify-center flex-wrap items-center gap-4 mt-5 2xl:mt-10">
          <div
            className="flex gap-1 items-center text-sm text-[#797979] font-semibold cursor-pointer"
            onClick={handleSelectAll}
          >
            <input
              type="checkbox"
              checked={selectAll}
              className="cursor-pointer"
            />{" "}
            Select All
          </div>
          <div
            className="flex gap-1 text-sm text-[#797979] font-semibold cursor-pointer"
            onClick={handleNonDownloadedSelection}
          >
            <input
              type="checkbox"
              checked={nonDownloadedSelect}
              className="cursor-pointer"
            />{" "}
            Select Non-downloaded
          </div>
          <div
            style={{ border: "1px solid #017C57" }}
            className="flex items-center gap-2 text-xs 2xl:text-sm text-[#017C57] font-bold px-4 py-2 rounded-full cursor-pointer"
            onClick={handlePreview}
          >
            <img src={EyeIcon} alt="eye-icon" /> PREVIEW
          </div>
          {isSubmitting ? (
            <div>
              <CircularProgress />
              {(netSpeed / 1024).toFixed(2)}KB/s
            </div>
          ) : (
            <>
              {!hideCompressedDownload && (
                <div
                  style={{ border: "1px solid #017C57" }}
                  className="flex items-center gap-2 text-xs 2xl:text-sm text-[#017C57] font-bold px-4 py-2 rounded-full cursor-pointer"
                  onClick={() => setRangeDownload(true)}
                >
                  <img
                    src={DownloadIcon}
                    alt="download-icon"
                    className="w-[18px] h-[18px] 2xl:w-[22px] 2xl:h-[22px]"
                  />

                  <div className="flex items-end gap-[2px]">
                    DOWNLOAD COMPRESSED
                    <div className="text-[8px] text-[#464646] align-bottom">
                      {selectedCount} Selected
                    </div>
                  </div>
                </div>
              )}
              <div
                style={{ border: "1px solid #017C57" }}
                className="flex items-center gap-2 text-xs 2xl:text-sm text-[#017C57] font-bold px-4 py-2 rounded-full cursor-pointer"
                onClick={() => handleDownload(false)}
              >
                <img
                  src={DownloadIcon}
                  alt="download-icon"
                  className=" w-[18px] h-[18px] 2xl:w-[22px] 2xl:h-[22px]"
                />
                <div className="flex items-end gap-[2px]">
                  DOWNLOAD ORIGINAL{" "}
                  <div className="text-[8px] text-[#464646] align-bottom">
                    {selectedCount} Selected
                  </div>
                </div>
              </div>{" "}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-5 gap-2 gap-y-4 mt-4 max-h-[52%] 2xl:max-h-[52%] overflow-y-scroll">
          {documents?.map((image, idx) => {
            return (
              <div
                key={idx}
                style={{ border: "1px solid #F9F9F9" }}
                className="group relative flex justify-center items-center w-[180px] h-[200px] bg-[#F9F9F9] rounded-[8px] overflow-hidden p-2"
                draggable
                onDragStart={() => (dragDocument.current = idx)}
                onDragEnter={() => (draggedOverDocument.current = idx)}
                onDragEnd={handleSort}
              >
                {" "}
                <div className="relative">
                  <div className="flex justify-between items-center  mb-1 pt-2">
                    <div className="absolute flex items-center gap-2 left-0 top-2">
                      {selectedDocEdit === idx ? (
                        <input
                          type="text"
                          className=" w-[25px] text-xs"
                          onChange={(e) => {
                            dragDocument.current = idx;
                            draggedOverDocument.current =
                              Number(e.target.value) - 1;
                            handleSort();
                            setSelectedDocEdit(null);
                          }}
                        />
                      ) : (
                        <div className="w-[25px] h-[25px] text-xs flex items-center justify-center bg-[white] rounded-full ">
                          {idx + 1}
                        </div>
                      )}
                      <img
                        src={EditIcon}
                        alt="edit-icon"
                        onClick={() => setSelectedDocEdit(idx)}
                        className="cursor-pointer"
                      />
                      {image?.isDownloaded && (
                        <div>
                          <img
                            src={DownloadedIcon}
                            width={18}
                            height={18}
                            alt="downloaded-icon"
                          />
                        </div>
                      )}
                    </div>

                    <div
                      onClick={() => handleSelection(idx)}
                      className="flex justify-end w-full"
                    >
                      <input
                        type="checkbox"
                        checked={image?.isSelected}
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                  <img
                    src={image?.documentUrl}
                    alt="document-img"
                    className="object-contain h-[170px] w-[140px]"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {isPreviewOpen && (
        <PreviewPdfModal
          documents={documents}
          setDocuments={setDocuments}
          setOpen={setPreviewOpen}
          isSubmitting={isSubmitting}
          generatePDF={generatePDFUsingPython}
        />
      )}
      (
      <Modal open={rangeDownload} onClose={() => setRangeDownload(false)}>
        <div className="fixed top-0 left-0 h-screen w-screen z-[1202] flex items-center justify-center">
          <div className="relative max-h-[85%] w-[50%] bg-white p-10 rounded-[20px] flex flex-col">
            {/* Close Icon */}
            <div
              className="absolute top-3 right-5 cursor-pointer"
              onClick={() => setRangeDownload(false)}
            >
              <img src={CloseIcon} alt="cross-icon" />
            </div>

            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 mt-4 shrink-0">
              <div className="flex flex-col min-w-[120px]">
                <label htmlFor="from_input" className="text-sm font-medium">
                  From
                </label>
                <input
                  id="from_input"
                  name="from_input"
                  type="number"
                  className={`mt-1 p-2 border ${
                    errorFrom ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  value={from}
                  min="0"
                  max="999"
                  onChange={(e) =>
                    handleNumberChange(e.target.value, setFrom, setErrorFrom)
                  }
                  onKeyDown={blockInvalidChars}
                />
                {errorFrom && (
                  <span className="text-red-500 text-xs mt-1">{errorFrom}</span>
                )}
              </div>

              <div className="flex flex-col min-w-[120px]">
                <label htmlFor="to_input" className="text-sm font-medium">
                  To
                </label>
                <input
                  id="to_input"
                  name="to_input"
                  type="number"
                  className={`mt-1 p-2 border ${
                    errorTo ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  value={to}
                  min="0"
                  max="999"
                  onChange={(e) =>
                    handleNumberChange(e.target.value, setTo, setErrorTo)
                  }
                  onKeyDown={blockInvalidChars}
                />
                {errorTo && (
                  <span className="text-red-500 text-xs mt-1">{errorTo}</span>
                )}
              </div>

              <div
                style={{ border: "1px solid #017C57" }}
                className="flex items-center gap-2 text-xs 2xl:text-sm text-[#017C57] font-bold px-4 py-2 rounded-full cursor-pointer"
                onClick={() => setRangePreview(!rangePreview)}
              >
                <img src={EyeIcon} alt="eye-icon" /> PREVIEW
              </div>

              <div
                className="text-white text-sm font-semibold px-6 py-2 bg-[#017C57] rounded-full cursor-pointer"
                onClick={() => {
                  handleDownloadUsingPyhton(true, from, to);
                  setRangeDownload(false);
                }}
              >
                Download
              </div>
            </div>

            {rangePreview && (
              <div className="mt-6 overflow-y-auto flex-1">
                <div>
                  {documents?.slice(from - 1, to)?.map((doc, idx) => {
                    return (
                      <div
                        key={idx}
                        className="mb-1 border border-solid border-[#e5e5e5]"
                      >
                        <img src={doc?.documentUrl} alt="image" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      )
    </div>
  );
};

export default DownloadMultipleDocuments;
